import type React from 'react';
import { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowIosLeft, ArrowIosRight } from '@shared/assets';
import { useWindowWidth } from '@shared/hooks';
import type { ExtendedGlide } from '@shared/types/products';
import { IconButton } from '@shared/ui';
import { useProduct } from '@src/entities/products';
import { LargeCarouselModal } from '../large-carousel';
import { PromoVideoModal } from '../promo-video-modal';
import { SmallCarousel } from '../small-carousel';
import { VideoBtn } from '../video-button';
import * as st from './styles.module.scss';

type Props = {
  id: number;
};

export const Preview: React.FC<Props> = ({ id }) => {
  const { product } = useProduct(id);
  const gliderRef = useRef<ExtendedGlide>(null);

  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);

  const images: string[] | undefined = product?.images.map((img) => img.url);

  const windowWidth = useWindowWidth();

  product?.promo_image
    ? images?.unshift(product?.promo_image.replace('https://platform-api.nicecode.biz', ''))
    : images;

  const countImages: number = images && images?.length > 0 ? images?.length : 0;

  const hasVideo = product && product.product_video;
  const scrollToSlide = gliderRef.current?.slide ? gliderRef.current?.slide + 1 : 0;

  const toggleVideo = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    setIsVideoOpen((p) => !p);
  };

  const toggleCarousel = (
    e?: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>
  ) => {
    e?.stopPropagation();
    setIsCarouselOpen((p) => !p);
  };

  return (
    <>
      <div className={st.preview} onClick={toggleCarousel}>
        {images && countImages > 1 && <SmallCarousel images={images} ref={gliderRef} />}
        {images && countImages === 1 && (
          <img className={st.preview__promo} src={product?.promo_image} alt={product?.title} />
        )}

        <div className={st.preview__buttons}>
          <div className={st.preview__video}>
            {hasVideo && (
              <NavLink to={'video#'}>
                <VideoBtn productId={id} />
              </NavLink>
            )}
          </div>
          {images && countImages > 1 && (
            <div className={st.preview__arrows}>
              <IconButton
                variant="transparent"
                id="buttonLeft"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
              >
                <ArrowIosLeft />
              </IconButton>
              <IconButton
                variant="transparent"
                id="buttonRight"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
              >
                <ArrowIosRight />
              </IconButton>
            </div>
          )}
        </div>
      </div>
      {hasVideo && <PromoVideoModal isOpen={isVideoOpen} onClose={toggleVideo} id={id} />}
      {images && countImages > 0 && windowWidth > 500 && (
        <LargeCarouselModal
          scrollTo={scrollToSlide}
          isOpen={isCarouselOpen}
          onRequestClose={toggleCarousel}
          images={images}
          productTitle={product.title}
        />
      )}
    </>
  );
};
